import { useEffect, useState } from "react";
import { api } from "../../lib/api";

/**
 * Per-page per-zone typography admin.
 * Writes CSS-variable overrides into MongoDB; the page applies them via
 * <style> tag injected when the page loads.
 *
 * Page list and zone list mirror what exists in CSS (.mk-page vars).
 */

const FAMILIES = [
  { label: "Default", value: "" },
  { label: "Fraunces (display)", value: "'Fraunces', serif" },
  { label: "Manrope (sans)", value: "'Manrope', sans-serif" },
  { label: "Outfit (sans, geometric)", value: "'Outfit', sans-serif" },
  { label: "Inter (sans, technical)", value: "'Inter', sans-serif" },
  { label: "Playfair Display (serif)", value: "'Playfair Display', serif" },
  { label: "Poppins (sans)", value: "'Poppins', sans-serif" },
];

const PAGES = [
  {
    id: "home",
    label: "Home",
    zones: [
      { id: "hero-line", label: "Hero cycling line", min: 32, max: 160, defSize: 120 },
      { id: "hero-subtext", label: "Hero subtext", min: 12, max: 28, defSize: 18 },
      { id: "section-label", label: "Section overline", min: 9, max: 14, defSize: 11 },
      { id: "section-title", label: "Section title", min: 24, max: 96, defSize: 64 },
      { id: "featured-title", label: "Featured work title", min: 18, max: 48, defSize: 32 },
      { id: "body", label: "Body / paragraph", min: 12, max: 22, defSize: 18 },
    ],
    cssPrefix: "hm",
  },
  {
    id: "work",
    label: "Works Grid",
    zones: [
      { id: "page-title", label: "Page title", min: 32, max: 128, defSize: 88 },
      { id: "filter-label", label: "Filter pill label", min: 9, max: 14, defSize: 11 },
      { id: "card-title", label: "Card title", min: 16, max: 40, defSize: 24 },
      { id: "card-meta", label: "Card meta (category · year)", min: 9, max: 14, defSize: 11 },
    ],
    cssPrefix: "wl",
  },
  {
    id: "case-studies",
    label: "Case Studies Grid",
    zones: [
      { id: "page-title", label: "Page title", min: 32, max: 128, defSize: 88 },
      { id: "card-title", label: "Card title", min: 16, max: 44, defSize: 28 },
      { id: "card-subtitle", label: "Card subtitle", min: 12, max: 20, defSize: 14 },
    ],
    cssPrefix: "csl",
  },
  {
    id: "nav",
    label: "Navigation Bar",
    zones: [
      { id: "logo", label: "Logo wordmark", min: 10, max: 22, defSize: 13 },
      { id: "link", label: "Nav link", min: 9, max: 16, defSize: 11 },
    ],
    cssPrefix: "nav",
  },
  {
    id: "marketing",
    label: "Marketing",
    zones: [
      { id: "hero-title", label: "Hero title", min: 32, max: 112, defSize: 96 },
      { id: "hero-body", label: "Hero body", min: 14, max: 28, defSize: 22 },
      { id: "section-label", label: "Section overline", min: 9, max: 14, defSize: 11 },
      { id: "section-title", label: "Section title", min: 24, max: 96, defSize: 64 },
      { id: "body", label: "Body text", min: 12, max: 22, defSize: 18 },
      { id: "final", label: "Final statement (italic)", min: 16, max: 36, defSize: 26 },
      { id: "pillar-title", label: "Pillar title", min: 18, max: 40, defSize: 28 },
      { id: "pillar-body", label: "Pillar body", min: 12, max: 18, defSize: 15 },
      { id: "quote", label: "Pull quote (italic)", min: 18, max: 44, defSize: 28 },
      { id: "card-title", label: "Card title", min: 18, max: 32, defSize: 24 },
    ],
    cssPrefix: "mk",
  },
  {
    id: "case-study",
    label: "Case Study (per project)",
    zones: [
      { id: "title", label: "Hero title", min: 36, max: 128, defSize: 96 },
      { id: "subtitle", label: "Subtitle", min: 12, max: 36, defSize: 24 },
      { id: "section-label", label: "Section overline", min: 9, max: 14, defSize: 11 },
      { id: "body", label: "Body text", min: 10, max: 28, defSize: 22 },
      { id: "quote", label: "Pull quote", min: 16, max: 64, defSize: 40 },
    ],
    cssPrefix: "cs",
  },
  {
    id: "about",
    label: "About",
    zones: [
      { id: "opening", label: "Opening headline", min: 24, max: 96, defSize: 88 },
      { id: "intro-body", label: "Intro body", min: 14, max: 36, defSize: 32 },
      { id: "section-label", label: "Section overline", min: 9, max: 14, defSize: 11 },
      { id: "body", label: "Body text", min: 14, max: 24, defSize: 18 },
    ],
    cssPrefix: "ab",
  },
];

export default function TypographyAdmin() {
  const [page, setPage] = useState(PAGES[0]);
  const [zones, setZones] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [works, setWorks] = useState([]);
  const [projectSlug, setProjectSlug] = useState(""); // "" = page-level overrides
  const [overriddenSlugs, setOverriddenSlugs] = useState(new Set());

  // Load works once for the case-study project picker
  useEffect(() => {
    api.get("/works").then(({ data }) => setWorks((data || []).filter((w) => w.slug)));
  }, []);

  // Load which slugs have their own typography saved (so we can flag them in
  // the dropdown → user immediately sees which projects the page-default
  // will NOT apply to)
  useEffect(() => {
    if (page.id !== "case-study" || works.length === 0) return;
    Promise.all(
      works.map((w) =>
        api
          .get(`/typography/case-study:${w.slug}`)
          .then(({ data }) => ({ slug: w.slug, hasZones: Object.values(data?.zones || {}).some((z) => Object.keys(z || {}).length > 0) }))
          .catch(() => ({ slug: w.slug, hasZones: false }))
      )
    ).then((results) => {
      setOverriddenSlugs(new Set(results.filter((r) => r.hasZones).map((r) => r.slug)));
    });
  }, [page.id, works]);

  // Compute the effective page_id (e.g. "case-study" or "case-study:my-project")
  const effectivePageId =
    page.id === "case-study" && projectSlug ? `case-study:${projectSlug}` : page.id;

  useEffect(() => {
    setLoading(true);
    api.get(`/typography/${effectivePageId}`).then(({ data }) => {
      setZones((data && data.zones) || {});
      setLoading(false);
    });
  }, [effectivePageId]);

  // Reset projectSlug when leaving the case-study tab
  useEffect(() => {
    if (page.id !== "case-study") setProjectSlug("");
  }, [page.id]);

  const setZone = (zid, patch) =>
    setZones((z) => ({ ...z, [zid]: { ...(z[zid] || {}), ...patch } }));

  const reset = (zid) => setZones((z) => ({ ...z, [zid]: {} }));
  const resetAll = () => setZones({});

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      await api.put(`/typography/${effectivePageId}`, { page_id: effectivePageId, zones });
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-typography" className="max-w-5xl space-y-10">
      <header>
        <p className="overline text-[#C9A050]/70 mb-3">Typography</p>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight">
          Per-zone typography
        </h1>
        <p className="text-[#8A8A93] text-sm mt-2 max-w-2xl">
          Override font, weight, size, line-height, letter-spacing, and italic
          per zone, per page. Each zone uses safe min/max ranges so layout never
          breaks. Leave a value blank/zero to inherit the page default.
        </p>
      </header>

      {/* Page tabs */}
      <div className="flex gap-2 border-b border-white/5">
        {PAGES.map((p) => (
          <button
            key={p.id}
            data-testid={`tg-page-${p.id}`}
            onClick={() => setPage(p)}
            className={`px-5 py-3 overline transition-colors ${
              p.id === page.id
                ? "text-[#C9A050] border-b-2 border-[#C9A050] -mb-px"
                : "text-[#8A8A93] hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Case-study project picker — page-level vs per-project overrides */}
      {page.id === "case-study" && (
        <div data-testid="tg-cs-scope" className="space-y-3">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="overline text-[#8A8A93]">Scope</span>
            <select
              value={projectSlug}
              onChange={(e) => setProjectSlug(e.target.value)}
              data-testid="tg-cs-project-select"
              className="bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-sm min-w-[280px]"
            >
              <option value="">All case studies (page default — used when a project has no own settings)</option>
              {works.map((w) => (
                <option key={w.slug} value={w.slug}>
                  {overriddenSlugs.has(w.slug) ? "● " : "○ "}{w.title} — /{w.slug}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-[#8A8A93] max-w-2xl leading-relaxed">
            {projectSlug ? (
              <>
                Editing typography for <span className="text-[#C9A050]">{projectSlug}</span> only.
                These values override the page default for this project.
                Hit "Reset zone" on any zone to remove that project-specific value.
              </>
            ) : (
              <>
                Editing the shared default. It applies to every case study that has NO own settings.
                Projects marked "●" in the dropdown have their own settings — the page default won't reach them until you reset those projects' zones.
              </>
            )}
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-[#8A8A93]">Loading…</p>
      ) : (
        <div className="space-y-5">
          {page.zones.map((z) => (
            <ZoneRow
              key={z.id}
              zone={z}
              value={zones[z.id] || {}}
              onChange={(patch) => setZone(z.id, patch)}
              onReset={() => reset(z.id)}
            />
          ))}
        </div>
      )}

      {/* Sticky save */}
      <div className="sticky bottom-0 bg-[#0A0A0C]/92 backdrop-blur-md py-4 border-t border-white/5 flex items-center gap-4 flex-wrap">
        <button
          onClick={save}
          disabled={busy}
          data-testid="tg-save"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button
          onClick={resetAll}
          className="overline border border-white/15 text-[#8A8A93] hover:text-white hover:border-white/40 px-5 py-3 transition-all"
          data-testid="tg-reset-all"
        >
          Reset entire page
        </button>
        {msg && (
          <span className="text-[#C9A050] text-sm" data-testid="tg-save-msg">
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}

function ZoneRow({ zone, value, onChange, onReset }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-xl p-5 md:p-6" data-testid={`tg-zone-${zone.id}`}>
      <div className="flex items-baseline justify-between mb-5 gap-4 flex-wrap">
        <h3 className="font-display text-xl md:text-2xl">{zone.label}</h3>
        <button
          onClick={onReset}
          className="text-xs text-[#8A8A93] hover:text-[#C9A050] transition-colors"
          data-testid={`tg-zone-reset-${zone.id}`}
        >
          Reset zone
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Family */}
        <div>
          <label className="overline block mb-2">Font family</label>
          <select
            value={value.family || ""}
            onChange={(e) => onChange({ family: e.target.value })}
            data-testid={`tg-${zone.id}-family`}
            className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050]"
          >
            {FAMILIES.map((f) => (
              <option key={f.label} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Weight */}
        <div>
          <label className="overline block mb-2">Weight</label>
          <select
            value={value.weight || 0}
            onChange={(e) => onChange({ weight: Number(e.target.value) })}
            data-testid={`tg-${zone.id}-weight`}
            className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050]"
          >
            <option value={0}>Default</option>
            <option value={300}>Light (300)</option>
            <option value={400}>Regular (400)</option>
            <option value={500}>Medium (500)</option>
            <option value={600}>Semibold (600)</option>
          </select>
        </div>

        {/* Size — Desktop */}
        <Slider
          label={`Size — desktop (px)  •  ${zone.min}–${zone.max}`}
          min={zone.min}
          max={zone.max}
          step={1}
          value={value.size_px || 0}
          fallback={zone.defSize}
          onChange={(v) => onChange({ size_px: v })}
          testid={`tg-${zone.id}-size`}
        />

        {/* Size — Mobile (independent override for <768px viewports) */}
        <Slider
          label={`Size — mobile (px)  •  ${Math.max(zone.min - 6, 8)}–${zone.max}  •  0 = same as desktop`}
          min={0}
          max={zone.max}
          step={1}
          value={value.size_px_mobile || 0}
          fallback={0}
          onChange={(v) => onChange({ size_px_mobile: v })}
          testid={`tg-${zone.id}-size-mobile`}
        />

        {/* Line height */}
        <Slider
          label="Line height"
          min={1.0}
          max={2.0}
          step={0.05}
          value={value.line_height || 0}
          fallback={1.5}
          onChange={(v) => onChange({ line_height: Number(v.toFixed(2)) })}
          testid={`tg-${zone.id}-leading`}
          decimals={2}
        />

        {/* Letter spacing */}
        <Slider
          label="Letter spacing (em)"
          min={-0.05}
          max={0.4}
          step={0.005}
          value={value.letter_spacing_em || 0}
          fallback={0}
          onChange={(v) => onChange({ letter_spacing_em: Number(v.toFixed(3)) })}
          testid={`tg-${zone.id}-tracking`}
          decimals={3}
        />

        {/* Italic */}
        <div>
          <label className="overline block mb-2">Italic</label>
          <select
            value={value.italic === true ? "1" : value.italic === false ? "0" : ""}
            onChange={(e) =>
              onChange({
                italic:
                  e.target.value === "1"
                    ? true
                    : e.target.value === "0"
                    ? false
                    : null,
              })
            }
            data-testid={`tg-${zone.id}-italic`}
            className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050]"
          >
            <option value="">Default</option>
            <option value="1">Italic</option>
            <option value="0">Not italic</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function Slider({ label, min, max, step, value, fallback, onChange, testid, decimals = 0 }) {
  const display = value === 0 || value === null || value === undefined
    ? `inherit (${fallback}${decimals === 0 ? "" : ""})`
    : decimals === 0
    ? value
    : Number(value).toFixed(decimals);
  return (
    <div>
      <label className="overline block mb-2 flex justify-between">
        <span>{label}</span>
        <span className="text-[#C9A050] font-medium">{display}</span>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value || fallback}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-[#C9A050]"
      />
    </div>
  );
}
